import * as React from 'react';

import { THEME_FALLING_EMOJIS, resolveThemeName, useTheme } from '../context/ThemeContext';

const PARTICLE_COUNT = 20;

interface Particle {
  emoji: string;
  left: number; // vw %
  size: number; // px
  duration: number; // s
  delay: number; // s (negative so the screen is already populated on load)
  drift: number; // px of horizontal sway
}

// Purely decorative: pointer-events are disabled, it's hidden from assistive tech, and
// themes.css hides it for users who prefer reduced motion.
export const FallingEmojis = () => {
  const { themeChoice, fxEnabled } = useTheme();
  const themeName = resolveThemeName(themeChoice);
  const emojis = THEME_FALLING_EMOJIS[themeName];

  const particles = React.useMemo<Particle[]>(
    () =>
      emojis.length === 0
        ? []
        : Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
            emoji: emojis[i % emojis.length],
            left: Math.random() * 100,
            size: 14 + Math.random() * 16,
            duration: 12 + Math.random() * 14,
            delay: -Math.random() * 26,
            drift: (Math.random() - 0.5) * 160,
          })),
    // Re-roll the particles whenever the theme changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [themeName]
  );

  const isActive = fxEnabled && particles.length > 0;

  if (!isActive) return null;

  return (
    <div className="bark-falling" aria-hidden="true">
      {particles.map((p, i) => (
        <span
          key={`${themeName}-${i}`}
          style={
            {
              left: `${p.left}%`,
              fontSize: `${p.size}px`,
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`,
              '--drift': `${p.drift}px`,
            } as React.CSSProperties
          }
        >
          {p.emoji}
        </span>
      ))}
    </div>
  );
};
